import { InmateNote } from '@reliance/types/models/InmateNote';
import { Inmate } from '@reliance/types/models/Inmate';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Button, Card } from 'react-bootstrap';
import { Form as FinalForm } from 'react-final-form';
import api from '../../common/api';
import { required } from '@reliance/forms-js/lib/validation';
import { TextArea } from '../../form/TextArea';
import { useInmateNotesByInmateId } from '../../hooks/InmateNote';
import BaseHtmlTable from '../../table/BaseHtmlTable';
import { useServerTableFilterState } from '../../table/useServerTableFilterState';
import { createColumnHelper } from '@tanstack/react-table';
import { useAlerts } from '../../alert/AlertContext';

const COLUMNS = [
  { id: 'date', meta: { sortName: 'dateEntered' } },
  { id: 'user', meta: { sortName: 'userName' } },
  { id: 'note', meta: { sortName: 'note' } },
];

type InmateNotesProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
};

type NoteFormValues = {
  note: string;
};

const InmateNotes: React.FC<InmateNotesProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger } = props;
  const alerts = useAlerts();
  const baseRecord = useMemo(() => ({ inmateId: inmate.id }), [inmate.id]);
  const tableState = useServerTableFilterState(COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 100 },
    initialSorting: [{ id: 'date', desc: true }],
  });

  const filters = useMemo(
    () => ({
      inmateId: inmate.id,
      page: tableState.mergedRecord.page as number,
      perPage: tableState.mergedRecord.perPage as number,
      orderBy: String(tableState.mergedRecord.orderBy ?? '-dateEntered'),
    }),
    [inmate.id, tableState.mergedRecord]
  );

  // Pass null to prevent auto-execution until the tab is selected
  // Pass filters so they're available when execute is called
  const notesResult = useInmateNotesByInmateId(
    null,
    filters
  ) as {
    result?: { data: InmateNote[]; total: number; page?: number; perPage?: number };
    loading: boolean;
    execute: (id: number) => void;
  };

  const executeRef = useRef(notesResult.execute);
  executeRef.current = notesResult.execute;

  useEffect(() => {
    if (!tabSelected || !inmate.id) {
      return;
    }
    void executeRef.current(inmate.id);
  }, [tabSelected, inmate.id, tableState.mergedRecord, refreshTrigger]);

  const handleAddNote = useCallback(async (values: NoteFormValues, form: any) => {
    try {
      await api.post('/inmate-note', { note: { note: values.note, entityId: inmate.id } });
      alerts.success('The note was successfully added');
      form.reset();
      void executeRef.current(inmate.id);
    } catch (error) {
      alerts.error('An error occurred while adding the note');
      throw error;
    }
  }, [inmate.id, alerts]);

  const columnHelper = createColumnHelper<InmateNote>();
  const columns = useMemo(() => [
    columnHelper.accessor((row) => {
      const date = typeof row.dateEntered === 'object' && row.dateEntered.date
        ? moment(row.dateEntered.date)
        : moment(row.dateEntered as any);
      return date.format('YYYY/MM/DD HH:mm:ss');
    }, {
      id: 'date',
      header: 'Date',
      enableSorting: true,
    }),
    columnHelper.accessor('userName', {
      id: 'user',
      header: 'User',
      enableSorting: true,
    }),
    columnHelper.accessor('note', {
      id: 'note',
      header: 'Note',
      enableSorting: true,
      cell: (info) => {
        const row = info.row.original;
        if (row.userName === 'System') {
          return <span dangerouslySetInnerHTML={{ __html: row.note }} />;
        }
        return <span>{row.note}</span>;
      },
    }),
  ], [columnHelper]);

  const notes = notesResult.result?.data || [];
  const total = notesResult.result?.total || 0;
  const page = notesResult.result?.page ?? tableState.pagination.pageIndex + 1;
  const perPage = notesResult.result?.perPage ?? tableState.pagination.pageSize;

  return (
    <>
      <Card className="mb-3">
        <Card.Header>
          Add Note
        </Card.Header>
        <FinalForm
          onSubmit={(values: NoteFormValues, form: any) => handleAddNote(values, form)}
          initialValues={{ note: '' }}
          render={({ handleSubmit, submitting, hasValidationErrors }) => (
            <form onSubmit={handleSubmit}>
              <Card.Body>
                <TextArea
                  name="note"
                  rows={3}
                  placeholder="Notes"
                  validationRules={[required]} />
              </Card.Body>
              <Card.Footer>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting || hasValidationErrors}>
                  {submitting ? 'Adding...' : 'Add Note'}
                </Button>
              </Card.Footer>
            </form>
          )} />
      </Card>
      <Card>
        <Card.Header>
          <h5>Inmate Notes</h5>
        </Card.Header>
        <Card.Body>
          <BaseHtmlTable
            rows={notes}
            columnDefs={columns}
            paginationMode="server"
            page={page}
            totalPages={Math.ceil(total / perPage)}
            total={total}
            perPage={perPage}
            loading={notesResult.loading}
            serverPagination={tableState.serverPagination}
            onServerPaginationChange={tableState.onServerPaginationChange}
            serverSorting={tableState.serverSorting}
            onServerSortingChange={tableState.onServerSortingChange}
            serverColumnFilters={tableState.serverColumnFilters}
            onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default InmateNotes;
